import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, BulkSelectCell, TableSkeleton,
  RowActions, RowAction, ListPagination, ListSearchInput, ListEmptyRow, BulkSelectHeader, MultiBulkActionButton,
  CustomSelect, StatusDot, Amount, btnPrimary,
} from '@redpanda/ui';
import {
  useBulkActions, createBulkDeleteOptimisticHandler, createBulkDeleteSuccessHandler, formatTableDate, isAdmin,
  listReturnUrl, useRefetchOnFocus, useRememberedState,
} from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import BankTransactionDialog from '../components/BankTransactionDialog';
import { BankTransaction, transactionStoreFor, useBankAccountDetail } from '../stores/bankAccountStore';
import { bankAccountService, bankReconciliationService } from '../services/api';
import { useListSearch } from '../hooks/useListSearch';
import { notifyDataChanged, useDataChanged } from '../utils/dataEvents';
import { pendingRowClass } from '../utils/pendingItem';
import { apiErrorMessage } from '../utils/apiError';

const COLUMNS = 7;
/** 發票收款、折讓退款產生的交易：要從發票刪除 */
const fromInvoice = (transaction: BankTransaction) => !!transaction.invoicePayment || !!transaction.creditNoteRefund;
type ReconciledFilter = 'ALL' | 'false' | 'true';
const RECONCILED_FILTERS: readonly ReconciledFilter[] = ['ALL', 'false', 'true'];

/**
 * 銀行帳戶的交易明細：麵包屑＋帳戶名稱（R20 詳情頁版型）；交易後餘額依日期累計（後端計算）
 * 已對帳的交易不能改、不能刪：列上只有「取消對帳」（自動比對配錯時用，同 Xero 的 Unreconcile）
 * 發票收款產生的交易也不能在這裡改刪：要從發票刪除收款（銀行交易、分錄、已收金額一起改）
 */
const BankTransactionPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { accountId = '' } = useParams<{ accountId: string }>();
  const { user } = useContext(UserContext)!;
  const userRole = user?.role;
  const admin = isAdmin(userRole);

  const storeBase = transactionStoreFor(accountId);
  const store = storeBase();
  const { items: transactions, pagination, isLoading, isPageLoading, showDeleteConfirmModal, isDeleting, fetchItems, setCurrentPage } = store;
  const account = useBankAccountDetail({ id: accountId });

  const [searchTerm, setSearchTerm] = useState('');
  const [reconciledFilter, setReconciledFilter] = useRememberedState<ReconciledFilter>('accounting-ui:bankTransactions.reconciled', 'ALL', RECONCILED_FILTERS);
  const [dialogTransaction, setDialogTransaction] = useState<BankTransaction | null | undefined>(undefined);
  const [unmatchTarget, setUnmatchTarget] = useState<BankTransaction | null>(null);
  const [unmatching, setUnmatching] = useState(false);

  useEffect(() => {
    fetchItems(1);
    account.refetch();
  }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps
  const refreshAll = () => {
    storeBase.getState().refreshData();
    account.refetch();
  };
  useRefetchOnFocus(refreshAll);
  useDataChanged(['bankTransactions', 'bankAccounts'], account.refetch);

  const search = searchTerm.trim();
  const reconciled = reconciledFilter === 'ALL' ? undefined : reconciledFilter;
  const hasFilters = !!search || !!reconciled;
  const searchResult = useListSearch<BankTransaction>(
    'bankTransactions',
    hasFilters ? { accountId, search, reconciled } : null,
    page => bankAccountService.getBankTransactions(accountId, page, 20, { search: search || undefined, reconciled })
  );
  const listItems = searchResult.active ? searchResult.items : transactions;
  const listPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;

  const bulkDeleteSuccess = createBulkDeleteSuccessHandler(() => storeBase.getState(), fetchItems, setCurrentPage);
  const bulkActions = useBulkActions({
    items: listItems,
    idField: 'id',
    entityName: t('bankTransactions.item'),
    bulkDeleteApi: (ids: string[]) => bankAccountService.bulkDeleteBankTransactions(accountId, ids),
    canDeleteItem: (transaction: BankTransaction) => !transaction.isReconciled && !fromInvoice(transaction),
    userRole,
    t,
    toast,
    pagination: listPagination ? { currentPage: listPagination.currentPage, totalPages: listPagination.totalPages } : undefined,
    fetchItems,
    setCurrentPage,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(storeBase.setState, `bankTransactions-${accountId}`, () => transactions, () => pagination),
    onSuccess: async (deletedIds?: string[]) => {
      await bulkDeleteSuccess(deletedIds ?? []);
      await storeBase.getState().refreshData(); // 交易後餘額重算
      notifyDataChanged('bankTransactions');
    },
  });
  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : bulkActions.handlePageChange(page));

  const handleUnmatch = async () => {
    if (!unmatchTarget) return;
    setUnmatching(true);
    try {
      await bankReconciliationService.unmatch(unmatchTarget.id);
      toast.success(t('bankTransactions.unmatch.success'));
      await storeBase.getState().refreshData();
      notifyDataChanged('bankTransactions');
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setUnmatching(false);
      setUnmatchTarget(null);
    }
  };

  const handleConfirmDelete = async () => {
    await store.confirmDelete();
    await storeBase.getState().refreshData();
    notifyDataChanged('bankTransactions');
  };

  const currency = account.data && account.data.currency !== 'TWD' ? `${account.data.currency} ` : undefined;
  const addButton = (
    <button type="button" onClick={() => setDialogTransaction(null)} className={btnPrimary}>
      {t('bankTransactions.addButton')}
    </button>
  );

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <nav className="mb-1 text-[13px] text-fg-secondary">
          <Link to={listReturnUrl('/bank-accounts')} className="hover:text-accent transition-colors">{t('bankAccounts.title')}</Link> /
        </nav>
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white truncate">
            {account.data?.accountName ?? <span className="text-fg-muted">—</span>}
          </h1>
          <div className="flex space-x-3 whitespace-nowrap">
            {admin && (
              <MultiBulkActionButton
                selectedCount={bulkActions.selectedItems.length}
                isDeleting={bulkActions.isBulkDeleting}
                onDelete={bulkActions.handleBulkDelete}
              />
            )}
            {addButton}
          </div>
        </div>
        {account.data && (
          <p className="mt-1 text-sm text-fg-secondary tabular-nums">
            {account.data.bankName} · {account.data.accountNumber}
            <span className="mx-2 text-fg-muted">·</span>
            {t('bankTransactions.currentBalance')} <span className="font-medium text-fg"><Amount value={Number(account.data.currentBalance)} currency={currency} /></span>
          </p>
        )}
      </div>

      <div className="mb-4 flex flex-col md:flex-row gap-3">
        <div className="md:w-80">
          <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('bankTransactions.searchPlaceholder')} />
        </div>
        <div className="md:w-40">
          <CustomSelect
            value={reconciledFilter}
            onChange={value => setReconciledFilter(value as ReconciledFilter)}
            options={RECONCILED_FILTERS.map(value => ({
              value,
              label: value === 'ALL' ? t('journals.list.allStatus') : t(value === 'true' ? 'bankTransactions.reconciled' : 'bankTransactions.unreconciled'),
            }))}
          />
        </div>
        {hasFilters && (
          <button
            type="button"
            onClick={() => { setSearchTerm(''); setReconciledFilter('ALL'); }}
            className="h-10 text-sm font-medium text-fg-secondary hover:text-fg self-start md:self-auto"
          >
            {t('common.filters.clear')}
          </button>
        )}
      </div>

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {(isLoading && transactions.length === 0 && !searchResult.active) || listLoading ? (
        <TableSkeleton rows={10} columns={COLUMNS} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '960px' }}>
              <TableHeader>
                <TableRow>
                  <BulkSelectHeader userRole={userRole} allSelected={bulkActions.allSelected} onToggleAll={bulkActions.toggleSelectAll} hasItems={listItems.length > 0} />
                  <TableHead numeric className="w-28">{t('bankTransactions.table.date')}</TableHead>
                  <TableHead>{t('bankTransactions.table.description')}</TableHead>
                  <TableHead className="w-24">{t('bankTransactions.table.type')}</TableHead>
                  <TableHead numeric className="w-36">{t('bankTransactions.table.amount')}</TableHead>
                  <TableHead numeric className="w-36">{t('bankTransactions.table.balanceAfter')}</TableHead>
                  <TableHead className="w-24">{t('bankTransactions.table.status')}</TableHead>
                  <TableHead className="w-28">{t('bankTransactions.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listItems.length === 0 ? (
                  <ListEmptyRow
                    colSpan={COLUMNS + 1}
                    filtered={hasFilters}
                    title={t('bankTransactions.empty')}
                    hint={t('bankTransactions.emptyHint')}
                    action={addButton}
                  />
                ) : (
                  listItems.map(transaction => (
                    <TableRow key={transaction.id} className={pendingRowClass(transaction)}>
                      <BulkSelectCell
                        userRole={userRole}
                        isSelected={bulkActions.selectedItems.includes(transaction.id)}
                        onToggle={() => bulkActions.toggleItemSelection(transaction.id)}
                        canDelete={!transaction.isReconciled && !fromInvoice(transaction)}
                        disabledReason={transaction.isReconciled ? t('errors.TRANSACTION_RECONCILED') : fromInvoice(transaction) ? t('errors.TRANSACTION_FROM_PAYMENT') : undefined}
                      />
                      <TableCell numeric>{formatTableDate(transaction.transactionDate, i18n.language)}</TableCell>
                      <TableCell className="truncate" title={transaction.description}>
                        <span className="text-fg">{transaction.description}</span>
                        {transaction.reference && <span className="block text-[13px] text-fg-secondary tabular-nums truncate">{transaction.reference}</span>}
                      </TableCell>
                      <TableCell>{t(`bankTransactions.types.${transaction.transactionType}`)}</TableCell>
                      <TableCell numeric><Amount value={Number(transaction.amount)} currency={currency} /></TableCell>
                      <TableCell numeric className="!text-fg-secondary"><Amount value={Number(transaction.balanceAfter)} currency={currency} /></TableCell>
                      <TableCell>
                        <StatusDot
                          tone={transaction.isReconciled ? 'success' : 'neutral'}
                          label={transaction.isReconciled ? t('bankTransactions.reconciled') : t('bankTransactions.unreconciled')}
                        />
                      </TableCell>
                      <TableCell>
                        {transaction.isReconciled ? (
                          <RowActions>
                            <RowAction onClick={() => setUnmatchTarget(transaction)}>{t('bankTransactions.unmatch.action')}</RowAction>
                          </RowActions>
                        ) : fromInvoice(transaction) ? (
                          <span className="text-[13px] text-fg-secondary" title={t('errors.TRANSACTION_FROM_PAYMENT')}>
                            {transaction.invoicePayment
                              ? t('bankTransactions.fromInvoice', { number: transaction.invoicePayment.invoice.number })
                              : t('bankTransactions.fromCreditNote', { number: transaction.creditNoteRefund!.number })}
                          </span>
                        ) : (
                          <RowActions>
                            <RowAction onClick={() => setDialogTransaction(transaction)}>{t('common.actions.edit')}</RowAction>
                            <RowAction tone="danger" onClick={() => store.handleDelete(transaction.id)}>{t('common.actions.delete')}</RowAction>
                          </RowActions>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={!!unmatchTarget}
        onClose={() => setUnmatchTarget(null)}
        onConfirm={handleUnmatch}
        title={t('bankTransactions.unmatch.title')}
        message={t('bankTransactions.unmatch.message')}
        confirmText={t('bankTransactions.unmatch.action')}
        cancelText={t('common.actions.cancel')}
        type="warning"
        isLoading={unmatching}
        loadingText={t('common.status.processing')}
        t={t}
      />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={store.closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title={t('bankTransactions.confirmDeleteTitle')}
        message={t('bankTransactions.confirmDeleteMessage')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <ConfirmModal
        isOpen={bulkActions.showBulkDeleteModal}
        onClose={bulkActions.closeBulkDeleteModal}
        onConfirm={bulkActions.confirmBulkDelete}
        title={t('bankTransactions.confirmBulkDeleteTitle', { count: bulkActions.selectedItems.length })}
        message={t('bankTransactions.confirmBulkDeleteMessage', { count: bulkActions.selectedItems.length })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={bulkActions.isBulkDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      {dialogTransaction !== undefined && (
        <BankTransactionDialog accountId={accountId} transaction={dialogTransaction ?? undefined} onClose={() => setDialogTransaction(undefined)} />
      )}
    </div>
  );
};

export default BankTransactionPage;
